/**
 * Privacy-first analytics helper (LGPD-compatible).
 * - Anonymous session id (UUID v4) persisted in localStorage.
 * - Fire-and-forget: errors are swallowed silently (analytics NEVER block UX).
 * - Posts to relative /api/events (new unified endpoint) with a fallback to
 *   legacy /api/track so nothing breaks if an old backend is running.
 * - Sends no PII: no email/name/phone/IP. User-agent is kept by the server
 *   (truncated at 256 chars) only for device classification.
 */
import { api } from "@/lib/api";

const SESSION_KEY = "cm_sid_v1";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function getDeviceType() {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth || 0;
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobi|iphone|ipod|android/.test(ua) && w < 900) return "mobile";
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function sanitizeMetadata(meta) {
  if (!meta || typeof meta !== "object") return undefined;
  const clean = {};
  const pii = new Set(["email", "name", "phone", "cpf", "ip"]);
  for (const k of Object.keys(meta)) {
    if (pii.has(k.toLowerCase())) continue;
    clean[k] = meta[k];
  }
  return Object.keys(clean).length ? clean : undefined;
}

/**
 * Track a custom event.
 * @param {string} event_type e.g. "click_buy_book", "click_plan", "click_sermon"
 * @param {object} [metadata] arbitrary non-PII metadata (sanitized client-side)
 */
export function track(event_type, metadata = undefined) {
  try {
    const path =
      typeof window !== "undefined"
        ? window.location.pathname + (window.location.hash || "")
        : null;
    const session_id = getSessionId();
    const device_type = getDeviceType();
    const cleanMeta = sanitizeMetadata(metadata);
    const body = {
      event_type,
      path,
      session_id,
      device_type,
      metadata: cleanMeta,
    };
    const config = { headers: { "Cache-Control": "no-store" } };
    api.post("/events", body, config).catch(() => {
      // Fallback to legacy /api/track shape so both endpoints stay interchangeable
      api
        .post(
          "/track",
          { event: event_type, page: path, session_id, meta: cleanMeta },
          config
        )
        .catch(() => {
          /* silent */
        });
    });
  } catch {
    /* silent */
  }
}

export function trackPageView() {
  track("page_view");
}
